/**
 * useThemePalette — returns the active premium theme's chart colours, read live
 * from CSS custom properties (--pm-s1..--pm-s6 set per theme in premium-skin.css).
 *
 * Returns `null` when the premium skin is off, so chart widgets fall back to
 * their original hardcoded palette (classic look unchanged). Re-reads on the
 * `v3-skin-change` event so switching themes recolours charts without a reload.
 */
import { useState, useEffect, useCallback } from 'react';

function readPalette() {
    if (typeof document === 'undefined') return null;
    if (!document.body.classList.contains('v3-premium')) return null;
    const cs = getComputedStyle(document.body);
    const v = (name, fallback) => {
        const raw = cs.getPropertyValue(name).trim();
        return raw || fallback;
    };
    return [
        v('--pm-s1', '#3B82F6'),
        v('--pm-s2', '#06B6D4'),
        v('--pm-s3', '#8B5CF6'),
        v('--pm-s4', '#10B981'),
        v('--pm-s5', '#F59E0B'),
        v('--pm-s6', '#EF4444'),
    ];
}

export function useThemePalette() {
    const [palette, setPalette] = useState(readPalette);

    const refresh = useCallback(() => {
        // Defer a tick so the body class / data-pm-theme attribute is applied
        // before we read the resolved custom properties.
        requestAnimationFrame(() => setPalette(readPalette()));
    }, []);

    useEffect(() => {
        window.addEventListener('v3-skin-change', refresh);
        return () => window.removeEventListener('v3-skin-change', refresh);
    }, [refresh]);

    return palette;
}
